import * as THREE from 'three'
import { useAppStore } from '../../stores/appStore'
import { rewriteTextureUrl } from '../../utils/textureUrl'
import { normalizeAssetUrl } from '../../config'

/**
 * Returns true if the texture has valid image data (e.g. image loaded).
 * Used to avoid setting needsUpdate on textures that would trigger WebGL "no image data" warning.
 */
export const isTextureReady = (tex: THREE.Texture | null | undefined): boolean => {
  if (!tex) return false
  const img = tex.image
  if (!img) return false
  if (typeof (img as HTMLImageElement).complete !== 'undefined') {
    return (img as HTMLImageElement).complete
  }
  return true
}

// Helper to pick texture URL from _urls dict or fallback to legacy _url field
export const pickTextureUrl = (urlsMap: any, fallback?: string | null): string | null => {
  if (!urlsMap && !fallback) return null
  
  const resPref = useAppStore.getState().textureResolution || '1k'

  let url: string | null = null
  if (urlsMap && typeof urlsMap === 'object') {
    url = urlsMap[resPref] || urlsMap['2k'] || urlsMap['1k'] || urlsMap['original'] || fallback || null
  } else if (fallback) {
    // If map not provided, rewrite original to preferred folder variant
    url = rewriteTextureUrl(fallback, resPref as any)
  }
  
  // Normalize HTTP to HTTPS if needed
  return url ? normalizeAssetUrl(url) : null
}

// Load all texture maps from a texture object with resolution selection
export const loadTextureMaps = (texture: any, loader: THREE.TextureLoader): {
  base: THREE.Texture | null
  normal: THREE.Texture | null
  roughness: THREE.Texture | null
  metalness: THREE.Texture | null
  ao: THREE.Texture | null
  displacement: THREE.Texture | null
} => {
  if (!texture) {
    return { base: null, normal: null, roughness: null, metalness: null, ao: null, displacement: null }
  }
  
  const baseUrl = pickTextureUrl(texture.base_color_urls, texture.base_color_url)
  const normalUrl = pickTextureUrl(texture.normal_urls, texture.normal_url)
  const roughnessUrl = pickTextureUrl(texture.roughness_urls, texture.roughness_url)
  const metalnessUrl = pickTextureUrl(texture.metalness_urls, texture.metalness_url)
  const aoUrl = pickTextureUrl(texture.ambient_occlusion_urls, texture.ambient_occlusion_url)
  const heightUrl = pickTextureUrl(texture.height_urls, texture.height_url)
  
  const loadTextureWithLogging = (url: string | null, mapType: string): THREE.Texture | null => {
    if (!url) {
      return null
    }
    const tex = loader.load(
      url,
      undefined,
      undefined,
      (error) => {
      }
    )
    return tex
  }
  
  return {
    base: loadTextureWithLogging(baseUrl, 'base'),
    normal: loadTextureWithLogging(normalUrl, 'normal'),
    roughness: loadTextureWithLogging(roughnessUrl, 'roughness'),
    metalness: loadTextureWithLogging(metalnessUrl, 'metalness'),
    ao: loadTextureWithLogging(aoUrl, 'ao'),
    displacement: loadTextureWithLogging(heightUrl, 'displacement'),
  }
}

export const ROTATE_QUARTER_TURNS_DEFAULT = 1
export const SWAP_UV_DEFAULT = true

export const rotateTexture = (tex: THREE.Texture | null | undefined, turns?: number) => {
  if (!tex) return
  const t = Math.abs(turns || 0) % 4
  if ((tex as any).center && typeof (tex as any).center.set === 'function') {
    ;(tex as any).center.set(0.5, 0.5)
  }
  tex.rotation = t ? (Math.PI / 2) * t : 0
}

export const setSRGBIfAvailable = (tex: THREE.Texture | null | undefined) => {
  if (!tex) return
  // @ts-ignore
  if ((THREE as any).SRGBColorSpace) {
    // @ts-ignore
    tex.colorSpace = (THREE as any).SRGBColorSpace
  }
}

export const swapMeshUV = (mesh: any, enabled: boolean, cacheKey: string) => {
  if (!enabled || !mesh?.geometry?.attributes?.uv) return
  const geom = mesh.geometry as THREE.BufferGeometry
  const uvAttr = geom.attributes.uv as THREE.BufferAttribute
  const key = `_origUV_${cacheKey}`
  if (!geom.userData[key]) {
    geom.userData[key] = uvAttr.array.slice(0)
  }
  const src: Float32Array = geom.userData[key] as Float32Array
  const uvs: Float32Array = new Float32Array(src.length)
  for (let i = 0; i < src.length; i += 2) {
    const u: number = Number(src[i])
    const v: number = Number(src[i + 1])
    uvs[i] = v
    uvs[i + 1] = u
  }
  geom.setAttribute('uv', new THREE.BufferAttribute(uvs, 2))
  ;(geom.attributes.uv as THREE.BufferAttribute).needsUpdate = true
}


// Shared PBR helpers to reduce duplication across components

export const cloneOrMakeStandard = (origMat: any): THREE.MeshStandardMaterial => {
  let base: any
  if (origMat && typeof origMat.clone === 'function') {
    base = origMat.clone()
  } else {
    base = new THREE.MeshStandardMaterial()
    if (origMat) {
      base.copy(origMat)
    }
  }
  // Always operate on a MeshStandardMaterial instance
  const standard = new THREE.MeshStandardMaterial().copy(base)
  return standard
}

export const prepareBaseColorTexture = (tex: THREE.Texture | null | undefined, turns: number = 0) => {
  if (!tex) return
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.flipY = false
  // Prefer modern colorSpace if available
  // @ts-ignore
  if ((THREE as any).SRGBColorSpace) {
    // @ts-ignore
    tex.colorSpace = (THREE as any).SRGBColorSpace
  }
  rotateTexture(tex, turns)
}

export const prepareLinearTexture = (tex: THREE.Texture | null | undefined, turns: number = 0) => {
  if (!tex) return
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.flipY = false
  // Mark as linear if supported helper exists
  // Roughness/metal/AO should remain linear
  rotateTexture(tex, turns)
}

export const copyRepeatOffsetRotationCenter = (dst: THREE.Texture, src?: THREE.Texture | null) => {
  if (!src || Array.isArray(src)) return
  dst.repeat.copy(src.repeat)
  dst.offset.copy(src.offset)
  ;(dst as any).rotation = (src as any).rotation
  if ((dst as any).center && (src as any).center) {
    ;(dst as any).center.copy((src as any).center)
  }
}

export const applyTextileDefaults = (mat: THREE.MeshStandardMaterial) => {
  // Fabric-like response
  mat.roughness = 0.9
  mat.metalness = 0.0
  mat.envMapIntensity = 0.6
  mat.aoMapIntensity = 1.0
}

// Global material defaults and helpers
export type MaterialType = 'textile' | 'wood' | 'metal' | 'leather'

export const MATERIAL_DEFAULTS: Record<MaterialType, { roughness: number; metalness: number; envMapIntensity: number; aoMapIntensity: number; normalScale: number }> = {
  // Textiles: slightly lower roughness and higher env intensity so
  // fabric picks up more shape and specular variation, combined with
  // a stronger normal scale.
  // Slightly smoother than before so textiles keep some sheen but
  // don't look plasticky.
  textile: { roughness: 0.85, metalness: 0.0,  envMapIntensity: 0.3,  aoMapIntensity: 1.2, normalScale: 5.0 },
  // Wood: a bit more pronounced normals so bedside tables and wood
  // details read less flat under studio lighting.
  wood:    { roughness: 0.8,  metalness: 0.0,  envMapIntensity: 0.3,  aoMapIntensity: 1.0, normalScale: 2.0 },
  metal:   { roughness: 0.3,  metalness: 1.0,  envMapIntensity: 1.0, aoMapIntensity: 1.0, normalScale: 1.0 },
  leather: { roughness: 0.6,  metalness: 0.05, envMapIntensity: 0.4, aoMapIntensity: 1.0, normalScale: 1.8 },
}

// Centralized base tint per material type
export const MATERIAL_TINTS: Record<MaterialType, number> = {
  textile: 0xdddddd,
  wood: 0xffffff,
  metal: 0xffffff,
  leather: 0xffffff,
}

export const applyMaterialDefaults = (mat: THREE.MeshStandardMaterial, type: MaterialType) => {
  const d = MATERIAL_DEFAULTS[type]
  mat.roughness = d.roughness
  mat.metalness = d.metalness
  ;(mat as any).envMapIntensity = d.envMapIntensity
  mat.aoMapIntensity = d.aoMapIntensity
  // Also set normal scale here so one call sets all visual defaults. Invert Y to match GLTF/FBX conventions.
  mat.normalScale = new THREE.Vector2(d.normalScale, -d.normalScale)
}

// normalScale is now applied inside applyMaterialDefaults; helper no longer needed

// Remove any legacy texture references coming from imported materials on a mesh
export const clearLegacyMapsOnMesh = (mesh: any) => {
  const clear = (m: any) => {
    if (!m) return
    m.map = null
    m.normalMap = null
    m.roughnessMap = null
    m.metalnessMap = null
    m.aoMap = null
    m.displacementMap = null
    // Reset base color/emissive/vertex colors so imported tints don't bleed through
    if (m.color) m.color.set(0xffffff)
    if (m.emissive) m.emissive.setRGB(0, 0, 0)
    m.vertexColors = false
    m.needsUpdate = true
  }
  const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
  mats.forEach(clear)
}

// Calculate UV-aware repeat values for textile textures
// Takes desired repeat (in meters, assuming 1m² texture) and mesh UV spans
export const calculateUVAwareRepeat = (
  mesh: THREE.Mesh,
  desiredRepeatX: number,
  desiredRepeatY: number,
  debug: boolean = false,
  normalizeUVSpan: boolean = true // Normalize UV spans to standard value for consistency
): { x: number; y: number } => {
  let uvSpanU = 1
  let uvSpanV = 1
  
  if (mesh.geometry?.attributes?.uv) {
    const uvAttr = mesh.geometry.attributes.uv as THREE.BufferAttribute
    const uvArray = uvAttr.array as Float32Array
    let minU = Infinity, maxU = -Infinity
    let minV = Infinity, maxV = -Infinity
    
    for (let i = 0; i < uvArray.length; i += 2) {
      const u = uvArray[i] ?? 0
      const v = uvArray[i + 1] ?? 0
      minU = Math.min(minU, u)
      maxU = Math.max(maxU, u)
      minV = Math.min(minV, v)
      maxV = Math.max(maxV, v)
    }
    
    uvSpanU = maxU - minU
    uvSpanV = maxV - minV
    
    // Normalize UV span to a standard value (average of common UV spans) for consistency
    // This ensures consistent texture density while still accounting for UV mapping
    if (normalizeUVSpan && uvSpanU > 0 && uvSpanV > 0) {
      // Use a standard UV span value (around 5-6 seems common based on the logs)
      // This normalizes different UV spans to a consistent value
      const STANDARD_UV_SPAN_U = 5.0
      const STANDARD_UV_SPAN_V = 5.0
      
      // Calculate what the desired repeat should be to achieve the same density
      // as if the UV span was the standard value
      const normalizedX = (desiredRepeatX / uvSpanU) * STANDARD_UV_SPAN_U
      const normalizedY = (desiredRepeatY / uvSpanV) * STANDARD_UV_SPAN_V
      
      if (debug) {
        const actualX = desiredRepeatX / uvSpanU
        const actualY = desiredRepeatY / uvSpanV
      }
      
      return {
        x: normalizedX,
        y: normalizedY
      }
    }
    
    if (debug) {
      // Debug mode - no logging needed
    }
  }
  
  // Divide desired repeat by UV span to account for existing UV scaling
  return {
    x: uvSpanU > 0 ? desiredRepeatX / uvSpanU : desiredRepeatX,
    y: uvSpanV > 0 ? desiredRepeatY / uvSpanV : desiredRepeatY
  }
}

// Ensure all secondary maps use the same repeat/offset/rotation/center as the base color map
export const unifyRepeatsFromBase = (mat: any) => {
  const base = mat?.map
  if (!base || Array.isArray(base)) return
  const applyFromBase = (tex?: THREE.Texture) => {
    if (!tex || Array.isArray(tex)) return
    copyRepeatOffsetRotationCenter(tex, base)
    tex.needsUpdate = true
  }
  applyFromBase(mat.normalMap)
  applyFromBase(mat.roughnessMap)
  applyFromBase(mat.metalnessMap)
  applyFromBase(mat.aoMap)
  applyFromBase(mat.displacementMap)
}



// Centralized PBR map application
export const applyPBR = (params: {
  mat: THREE.MeshStandardMaterial
  origMat?: any
  type: MaterialType
  meshName: string
  maps: {
    base?: THREE.Texture | null
    normal?: THREE.Texture | null
    roughness?: THREE.Texture | null
    metalness?: THREE.Texture | null
    ao?: THREE.Texture | null
    displacement?: THREE.Texture | null
  }
  rotateTurns?: number
  hasUV2?: boolean
  tintColorHex?: number
  repeat?: { x: number; y: number }
  offset?: { x: number; y: number }
  rotation?: number
}) => {
  const { mat, origMat, type, meshName, maps, rotateTurns = 0, hasUV2, tintColorHex, repeat, offset, rotation } = params
  applyMaterialDefaults(mat, type)

  // Base color
  if (maps.base) {
    const base = maps.base
    base.wrapS = THREE.RepeatWrapping
    base.wrapT = THREE.RepeatWrapping
    base.flipY = false
    setSRGBIfAvailable(base)
    rotateTexture(base, rotateTurns)
    if (repeat) base.repeat.set(repeat.x, repeat.y)
    if (offset) base.offset.set(offset.x, offset.y)
    if (typeof rotation === 'number') (base as any).rotation = rotation
    mat.map = base
    mat.needsUpdate = true
    // Use provided tint or centralized default per type
    const defaultTint = MATERIAL_TINTS[type]
    mat.color = new THREE.Color(typeof tintColorHex === 'number' ? tintColorHex : defaultTint)
    // Ensure no emissive bleed and ignore vertex colors from import
    if ((mat as any).emissive) (mat as any).emissive.setRGB(0, 0, 0)
    ;(mat as any).vertexColors = false
  }
  else {
    // Even without a base map, normalize base color so legacy tints don't leak
    const defaultNoMapTint = MATERIAL_TINTS[type]
    mat.color = new THREE.Color(typeof tintColorHex === 'number' ? tintColorHex : defaultNoMapTint)
    if ((mat as any).emissive) (mat as any).emissive.setRGB(0, 0, 0)
    ;(mat as any).vertexColors = false
  }

  // Normal
  if (maps.normal) {
    const nm = maps.normal
    nm.wrapS = THREE.RepeatWrapping
    nm.wrapT = THREE.RepeatWrapping
    nm.flipY = false
    // Non-color data should be linear
    ;(nm as any).colorSpace = (THREE as any).LinearSRGBColorSpace ?? (THREE as any).LinearEncoding
    rotateTexture(nm, rotateTurns)
    if (repeat) nm.repeat.set(repeat.x, repeat.y)
    if (offset) nm.offset.set(offset.x, offset.y)
    if (typeof rotation === 'number') (nm as any).rotation = rotation
    mat.normalMap = nm
    // Ensure normalScale is set (should already be set by applyMaterialDefaults, but verify)
    const d = MATERIAL_DEFAULTS[type]
    mat.normalScale = new THREE.Vector2(d.normalScale, -d.normalScale)
  }

  // Roughness
  if (maps.roughness) {
    const rm = maps.roughness
    rm.wrapS = THREE.RepeatWrapping
    rm.wrapT = THREE.RepeatWrapping
    rm.flipY = false
    ;(rm as any).colorSpace = (THREE as any).LinearSRGBColorSpace ?? (THREE as any).LinearEncoding
    rotateTexture(rm, rotateTurns)
    if (repeat) rm.repeat.set(repeat.x, repeat.y)
    if (offset) rm.offset.set(offset.x, offset.y)
    if (typeof rotation === 'number') (rm as any).rotation = rotation
    mat.roughnessMap = rm
  }

  // Metalness
  if (maps.metalness) {
    const mm = maps.metalness
    mm.wrapS = THREE.RepeatWrapping
    mm.wrapT = THREE.RepeatWrapping
    mm.flipY = false
    ;(mm as any).colorSpace = (THREE as any).LinearSRGBColorSpace ?? (THREE as any).LinearEncoding
    rotateTexture(mm, rotateTurns)
    if (repeat) mm.repeat.set(repeat.x, repeat.y)
    if (offset) mm.offset.set(offset.x, offset.y)
    if (typeof rotation === 'number') (mm as any).rotation = rotation
    mat.metalnessMap = mm
  }

  // AO (requires uv2)
  if (maps.ao && hasUV2) {
    const aom = maps.ao
    aom.wrapS = THREE.RepeatWrapping
    aom.wrapT = THREE.RepeatWrapping
    aom.flipY = false
    ;(aom as any).colorSpace = (THREE as any).LinearSRGBColorSpace ?? (THREE as any).LinearEncoding
    rotateTexture(aom, rotateTurns)
    if (repeat) aom.repeat.set(repeat.x, repeat.y)
    if (offset) aom.offset.set(offset.x, offset.y)
    if (typeof rotation === 'number') (aom as any).rotation = rotation
    mat.aoMap = aom
  }

  // Displacement
  if ((maps as any).displacement) {
    const dm = (maps as any).displacement as THREE.Texture
    dm.wrapS = THREE.RepeatWrapping
    dm.wrapT = THREE.RepeatWrapping
    dm.flipY = false
    ;(dm as any).colorSpace = (THREE as any).LinearSRGBColorSpace ?? (THREE as any).LinearEncoding
    rotateTexture(dm, rotateTurns)
    if (repeat) dm.repeat.set(repeat.x, repeat.y)
    if (offset) dm.offset.set(offset.x, offset.y)
    if (typeof rotation === 'number') (dm as any).rotation = rotation
    ;(mat as any).displacementMap = dm
    ;(mat as any).displacementScale = 0.001
    ;(mat as any).displacementBias = 0
  }

  unifyRepeatsFromBase(mat)
}

// Create a fresh, neutralized MeshStandardMaterial
export const makeFreshStandardMaterial = (type: MaterialType, tintColorHex?: number): THREE.MeshStandardMaterial => {
  const mat = new THREE.MeshStandardMaterial()
  // Neutralize imported state
  mat.color = new THREE.Color(typeof tintColorHex === 'number' ? tintColorHex : 0xffffff)
  ;(mat as any).emissive?.setRGB?.(0, 0, 0)
  ;(mat as any).vertexColors = false
  applyMaterialDefaults(mat, type)
  return mat
}

// Apply PBR maps to all materials on a mesh using a single maps set
export const applyPBRToMesh = (child: any, type: MaterialType, maps: {
  base?: THREE.Texture | null
  normal?: THREE.Texture | null
  roughness?: THREE.Texture | null
  metalness?: THREE.Texture | null
  ao?: THREE.Texture | null
  displacement?: THREE.Texture | null
}, rotateTurns = 0) => {
  if (!child?.isMesh || !child.material) return
  clearLegacyMapsOnMesh(child)
  const isArray = Array.isArray(child.material)
  const origMats = isArray ? child.material : [child.material]
  const hasUV2 = !!child.geometry?.attributes?.uv2
  const newMaterials = origMats.map((origMat: any) => {
    const mat = makeFreshStandardMaterial(type)
    applyPBR({ mat, origMat, type, meshName: child.name, maps, rotateTurns, hasUV2 })
    return mat
  })
  child.material = isArray ? newMaterials : newMaterials[0]
}

// Standardized PBR texture loader from a texture object that uses the API field names
export type PBRTextureURLs = {
  base_color_url?: string | null
  base_color_urls?: { '1k'?: string; '2k'?: string; original?: string } | null
  normal_url?: string | null
  normal_urls?: { '1k'?: string; '2k'?: string; original?: string } | null
  roughness_url?: string | null
  roughness_urls?: { '1k'?: string; '2k'?: string; original?: string } | null
  metalness_url?: string | null
  metalness_urls?: { '1k'?: string; '2k'?: string; original?: string } | null
  ambient_occlusion_url?: string | null
  ambient_occlusion_urls?: { '1k'?: string; '2k'?: string; original?: string } | null
  height_url?: string | null
  height_urls?: { '1k'?: string; '2k'?: string; original?: string } | null
}

export const loadPBRTextures = (urls: PBRTextureURLs) => {
  // Return null for all maps to avoid "no image data" errors
  // Textures should be loaded via useLoader in components, not here
  return {
    base: null,
    normal: null,
    roughness: null,
    metalness: null,
    ao: null,
    displacement: null,
  }
}


